import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Seo, SITE, breadcrumb } from "@/components/Seo";
import { toast } from "sonner";
import { CheckCircle2, ShieldCheck, Download, Loader2, ArrowLeft, FileText, PhoneCall } from "lucide-react";

const FAQS = [
  { q: "What format are the documents in?", a: "Editable Microsoft Word and Excel files plus print-ready PDFs — so you can add your logo, adjust wording, and deploy them across your store." },
  { q: "Are these ready to use out of the box?", a: "Yes. They're built from real dealership operations. You should still review and customize them for your state/jurisdiction, OEM requirements, and company policy before rolling them out." },
  { q: "How do I get access after buying?", a: "Instantly. Everything appears in your member portal to download right away, with lifetime access and free updates." },
  { q: "What if it's not a fit?", a: "You're covered by a 30-day money-back guarantee. If it's not right for your store, email us within 30 days for a full refund." },
];

export default function ProductDetail() {
  const { slug } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(false);
  const [lead, setLead] = useState({ name: "", dealership: "", email: "", phone: "", message: "" });
  const [leadSent, setLeadSent] = useState(false);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    api.get(`/products/${slug}`).then((r) => setProduct(r.data)).catch(() => setProduct(false));
  }, [slug]);

  const buy = async () => {
    if (!user) { navigate("/login", { state: { from: `/products/${slug}` } }); return; }
    setLoading(true);
    try {
      const { data } = await api.post("/checkout/session", { product_id: product.id, origin_url: window.location.origin });
      window.location.href = data.url;
    } catch { toast.error("Could not start checkout. Please try again."); setLoading(false); }
  };

  const submitLead = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post("/leads/enterprise", lead);
      setLeadSent(true);
      toast.success("Request received — we'll be in touch shortly.");
    } catch { toast.error("Could not send. Please try again."); }
    finally { setSending(false); }
  };

  if (product === false) return <div className="max-w-[1280px] mx-auto px-6 py-32 text-center">Product not found.</div>;
  if (!product) return <div className="max-w-[1280px] mx-auto px-6 py-32 flex justify-center"><Loader2 className="animate-spin text-primary" /></div>;

  const isCdos = product.id === "cdos";
  const showTrust = product.tier_level >= 2;

  const productSchema = {
    "@context": "https://schema.org",
    "@type": "Product",
    name: product.name,
    description: (product.tagline || product.description || "").slice(0, 300),
    brand: { "@type": "Brand", name: "Centennial Business Systems" },
    offers: {
      "@type": "Offer",
      price: Number(product.price) || 0,
      priceCurrency: "USD",
      availability: "https://schema.org/InStock",
      url: `${SITE}/products/${slug}`,
    },
  };
  const productJsonLd = [
    productSchema,
    breadcrumb([
      { name: "Home", path: "/" },
      { name: "Products", path: "/products" },
      { name: product.name, path: `/products/${slug}` },
    ]),
    {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: FAQS.map((f) => ({
        "@type": "Question",
        name: f.q,
        acceptedAnswer: { "@type": "Answer", text: f.a },
      })),
    },
  ];

  return (
    <div className="max-w-[1280px] mx-auto px-6 lg:px-8 py-16">
      <Seo
        title={`${product.name} | Centennial Dealership Operating System`}
        description={(product.tagline || product.description || "").slice(0, 160)}
        canonicalPath={`/products/${slug}`}
        type="product"
        jsonLd={productJsonLd}
      />
      <Link to="/products" className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-primary mb-10">
        <ArrowLeft className="w-4 h-4" /> All products
      </Link>

      <div className="grid lg:grid-cols-12 gap-12">
        <div className="lg:col-span-7">
          {product.badge && (
            <span className="inline-block text-[11px] uppercase tracking-wider font-semibold px-2.5 py-1 bg-primary text-white mb-5">{product.badge}</span>
          )}
          <h1 className="font-heading font-black text-4xl lg:text-5xl tracking-tighter leading-[1.05]">{product.name}</h1>
          <p className="text-lg text-muted-foreground mt-5 leading-relaxed">{product.description}</p>

          <h3 className="font-heading font-semibold text-lg mt-12 mb-5">What's included</h3>
          <ul className="grid sm:grid-cols-2 gap-4">
            {product.features?.map((f) => (
              <li key={f} className="flex items-start gap-2.5">
                <CheckCircle2 className="w-5 h-5 text-primary shrink-0 mt-0.5" />
                <span className="text-foreground/90">{f}</span>
              </li>
            ))}
          </ul>

          {product.doc_count > 0 && (
            <div className="mt-10 flex items-center gap-2 text-sm text-muted-foreground font-mono">
              <Download className="w-4 h-4" /> {product.doc_count} downloadable documents in this package
            </div>
          )}

          {/* WHAT'S INSIDE PREVIEW */}
          {showTrust && product.sample_docs?.length > 0 && (
            <div className="mt-12" data-testid="whats-inside">
              <h3 className="font-heading font-semibold text-lg mb-4">A sample of what's inside</h3>
              <div className="grid sm:grid-cols-2 gap-x-8 gap-y-2 border border-border bg-secondary/40 p-6">
                {product.sample_docs.map((t, i) => (
                  <div key={i} className="flex items-start gap-2 text-sm text-foreground/80">
                    <FileText className="w-4 h-4 text-primary/70 shrink-0 mt-0.5" /> <span>{t}</span>
                  </div>
                ))}
              </div>
              <p className="text-xs text-muted-foreground mt-2">…and {Math.max(product.doc_count - product.sample_docs.length, 0)}+ more across every department.</p>
            </div>
          )}

          {/* FAQ */}
          {showTrust && (
            <div className="mt-12" data-testid="product-faq">
              <h3 className="font-heading font-semibold text-lg mb-4">Questions, answered</h3>
              <div className="divide-y divide-border border border-border">
                {FAQS.map((f, i) => (
                  <details key={i} className="group p-5" data-testid={`faq-${i}`}>
                    <summary className="cursor-pointer font-semibold text-sm list-none flex items-center justify-between">
                      {f.q}<span className="text-primary group-open:rotate-45 transition-transform text-lg leading-none">+</span>
                    </summary>
                    <p className="text-sm text-muted-foreground mt-3 leading-relaxed">{f.a}</p>
                  </details>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* BUY / REQUEST BOX */}
        <div className="lg:col-span-5">
          <div className="lg:sticky lg:top-24 border border-border bg-white p-8">
            <div className="font-heading font-black text-5xl tracking-tight">
              {isCdos && <span className="text-2xl font-sans font-normal text-muted-foreground align-middle">from </span>}
              ${product.price.toLocaleString()}
            </div>
            <div className="text-sm text-muted-foreground mt-1">
              {isCdos ? "Tailored to your store — let's scope it together" : "One-time payment · lifetime access"}
            </div>

            {isCdos ? (
              leadSent ? (
                <div className="mt-7 text-center py-6" data-testid="lead-success">
                  <CheckCircle2 className="w-10 h-10 text-primary mx-auto" />
                  <p className="font-semibold mt-3">Request received</p>
                  <p className="text-sm text-muted-foreground mt-1">We'll reach out to scope your dealership operating system.</p>
                </div>
              ) : (
                <form onSubmit={submitLead} className="mt-6 space-y-3" data-testid="cdos-lead-form">
                  <input required placeholder="Your name" data-testid="lead-name" value={lead.name}
                    onChange={(e) => setLead({ ...lead, name: e.target.value })}
                    className="w-full border border-border px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary" />
                  <input placeholder="Dealership" data-testid="lead-dealership" value={lead.dealership}
                    onChange={(e) => setLead({ ...lead, dealership: e.target.value })}
                    className="w-full border border-border px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary" />
                  <input required type="email" placeholder="Work email" data-testid="lead-email" value={lead.email}
                    onChange={(e) => setLead({ ...lead, email: e.target.value })}
                    className="w-full border border-border px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary" />
                  <input placeholder="Phone (optional)" data-testid="lead-phone" value={lead.phone}
                    onChange={(e) => setLead({ ...lead, phone: e.target.value })}
                    className="w-full border border-border px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary" />
                  <textarea placeholder="What are you trying to systemize? (optional)" data-testid="lead-message" rows={3} value={lead.message}
                    onChange={(e) => setLead({ ...lead, message: e.target.value })}
                    className="w-full border border-border px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary" />
                  <button type="submit" disabled={sending} data-testid="lead-submit"
                    className="w-full inline-flex items-center justify-center gap-2 bg-primary text-white py-4 font-semibold btn-lift disabled:opacity-60">
                    {sending ? <><Loader2 className="w-4 h-4 animate-spin" /> Sending…</> : <><PhoneCall className="w-4 h-4" /> Request access / Book a call</>}
                  </button>
                  <p className="text-xs text-muted-foreground text-center">No card, no obligation — a real conversation about your store.</p>
                </form>
              )
            ) : (
              <>
                <button onClick={buy} disabled={loading} data-testid="product-buy-btn"
                  className="w-full mt-7 inline-flex items-center justify-center gap-2 bg-primary text-white py-4 font-semibold btn-lift disabled:opacity-60">
                  {loading ? <><Loader2 className="w-4 h-4 animate-spin" /> Redirecting…</> : "Buy now"}
                </button>
                {!user && <p className="text-xs text-muted-foreground mt-3 text-center">You'll create an account at checkout to access your downloads.</p>}
              </>
            )}

            <div className="mt-7 pt-6 border-t border-border space-y-3 text-sm">
              <div className="flex items-center gap-2.5 text-foreground/80 font-semibold">
                <ShieldCheck className="w-4 h-4 text-primary" /> 30-day money-back guarantee
              </div>
              {["Instant access in your member portal", "Editable & brandable files", "Free updates included", "Secure Stripe checkout"].map((t) => (
                <div key={t} className="flex items-center gap-2.5 text-foreground/80">
                  <ShieldCheck className="w-4 h-4 text-primary/60" /> {t}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
